import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import type { BaseButtonStyle } from "components/designV2/buttons/baseButton";
import { Modal } from "components/designV2/modal/modal";
import { TextArea } from "components/designV2/textarea/textarea";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

interface ActionCommentModalProps {
  isOpen: boolean;
  title: string;
  confirmLabel: string;
  confirmIconName?: string;
  confirmStyleOverrides?: Partial<BaseButtonStyle>;
  isSubmitting?: boolean;
  onClose: () => void;
  onConfirm: (comment: string) => Promise<boolean> | boolean;
}

export const ActionCommentModal = (
  props: ActionCommentModalProps,
): JSX.Element => {
  const { t } = useTranslation();
  const [comment, setComment] = useState("");
  const [isSubmittingLocal, setIsSubmittingLocal] = useState(false);
  const isBusy = Boolean(props.isSubmitting) || isSubmittingLocal;

  useEffect(() => {
    if (!props.isOpen) return;
    setComment("");
    setIsSubmittingLocal(false);
  }, [props.isOpen]);

  const handleSubmit = async (): Promise<void> => {
    if (isBusy) return;
    setIsSubmittingLocal(true);
    try {
      const result = await Promise.resolve(props.onConfirm(comment.trim()));
      if (result === false) return;
      props.onClose();
    } catch {
      return;
    } finally {
      setIsSubmittingLocal(false);
    }
  };

  const handleClose = (): void => {
    if (isBusy) return;
    props.onClose();
  };

  return (
    <Modal
      label={props.title}
      isOpen={props.isOpen}
      onClose={handleClose}
      size="small"
      showCloseButton={false}
      contentOverflow="visible"
      footer={
        <ActionRow>
          <SecondaryButton
            label={t("CompanyTimeReport.Cancel")}
            disabled={isBusy}
            onClick={handleClose}
          />
          <PrimaryButton
            label={props.confirmLabel}
            iconName={props.confirmIconName}
            styleOverrides={props.confirmStyleOverrides}
            isLoading={isBusy}
            disabled={isBusy}
            onClick={handleSubmit}
          />
        </ActionRow>
      }
    >
      <ModalBody>
        <TextArea
          label={t("Schedule.Comment")}
          value={comment}
          onChange={setComment}
          placeholder={t("Schedule.Comment")}
          maxHeight={240}
        />
      </ModalBody>
    </Modal>
  );
};

//Move out if styles get bigger
const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  height: 100%;
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  width: 100%;
`;
